// Shared display helpers for booking window status / ticket decision and
// service-type labels. Used by Bookings.vue, bookingDetailDialog and SmsLogs.vue
// so the vocabulary and the general.queues lookup live in one place.
export default {
  methods: {
    serviceTypeDisplay(service_type_id) {
      if (!service_type_id) return '-'
      const queue = this.general.queues.find(q => q.service_type_id === service_type_id)
      if (!queue) return service_type_id
      return queue.prefix ? `${queue.service_type} (${queue.prefix})` : queue.service_type
    },
    windowStatusLabel(status) {
      const map = {
        within_window: 'Within Window',
        early: 'Early',
        late: 'Late',
        not_booking: 'Not Booking'
      }
      return map[status] || status || '-'
    },
    windowStatusColor(status) {
      const map = {
        within_window: 'success',
        early: 'orange',
        late: 'error',
        not_booking: 'grey'
      }
      return map[status] || 'grey'
    },
    ticketDecisionLabel(decision) {
      const map = {
        booking: 'Booking',
        walkin: 'Walk-in'
      }
      return map[decision] || '-'
    },
    // The "Profile / Service Type" column promises both facts, so it renders both: an either/or
    // fallback hid the service type on every row that had a profile, which is most of them.
    // Both halves are persisted import facts, which is what keeps this column consistent with
    // the persisted Ticket Decision beside it — see bookedServiceTypeDisplay().
    profileDisplay(item) {
      const profile = (item && item.profile_name) || null
      const service_type = this.bookedServiceTypeDisplay(item)
      return profile ? `${profile} / ${service_type}` : service_type
    },
    // The queue this booking was IMPORTED into, straight from the persisted column.
    //
    // Deliberately not `effective_service_type_id`: the backend recomputes that one from the
    // clock on every request (enrich_booking()), so the same row answers differently before and
    // after the check-in window. Rendering it beside a persisted ticket decision made an early
    // booking that was issued a walk-in ticket display its reserved queue next to "Walk-in".
    bookedServiceTypeDisplay(booking) {
      return this.serviceTypeDisplay(booking && booking.service_type_id)
    },
    // Where this booking would be routed if the customer checked in RIGHT NOW — a live value,
    // only meaningful until a ticket exists. Always label it as such where it is rendered.
    liveServiceTypeDisplay(booking) {
      return this.serviceTypeDisplay(booking && booking.effective_service_type_id)
    },
    // Has a ticket already been issued for this booking? Shared with the detail dialog so the
    // list and the dialog cannot disagree about it.
    //
    // `ticket_id` / `ticket_user_id` / `ticket_decision` all arrived with v2; `is_created_ticket`
    // predates them and was never backfilled, so a booking ticketed by the old flow has the flag
    // set and the three v2 columns null.
    hasIssuedTicket(item) {
      return !!(item && (item.ticket_id || item.ticket_user_id || item.ticket_code || item.is_created_ticket))
    },
    // THE renderer for "which ticket was created for this booking". One function rather than a
    // chain of template branches, so it cannot disagree with hasIssuedTicket(): every identifier
    // it can print is also an identifier hasIssuedTicket() accepts, and the only remaining exit is
    // guarded by hasIssuedTicket() itself. `createdTicketDisplay(x) === '-'` is therefore
    // equivalent to `!hasIssuedTicket(x)` by construction, not by anyone remembering to keep two
    // lists in step — which is exactly how the list came to call a migrated ticket absent while
    // the detail dialog showed its Linked Ticket section.
    createdTicketDisplay(item) {
      if (item && item.ticket_code) return item.ticket_code
      if (item && item.ticket_id) return `#${item.ticket_id}`
      if (item && item.ticket_user_id) return `#${item.ticket_user_id} (ticket user)`
      // A ticket was issued before the v2 columns existed, and the migration did not backfill
      // them. Saying "-" here would deny a ticket the operator can see in the detail dialog.
      if (this.hasIssuedTicket(item)) return 'Created (legacy - ID unavailable)'
      return '-'
    },
    // List-cell form of the same distinction: the decision persisted when the ticket was issued,
    // or — while no ticket exists — the backend's clock-derived preview, explicitly marked so it
    // is never read as a record of what happened.
    //
    // A booking that HAS a ticket but no persisted decision (a pre-v2 row) gets neither: the
    // preview is a statement about now, and offering it beside an already-issued ticket is the
    // exact confusion this method exists to prevent. The decision was never recorded, so say so.
    ticketDecisionDisplay(item) {
      if (item && item.ticket_decision) return this.ticketDecisionLabel(item.ticket_decision)
      if (this.hasIssuedTicket(item)) return '-'
      const preview = this.ticketDecisionLabel(item && item.effective_ticket_decision)
      return preview === '-' ? preview : `${preview} (if now)`
    }
  }
}
